import { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";

type StyleObject = SystemStyleObject<Theme>;
type StyleGroup = Record<string, StyleObject>;
type Styles = Record<string, StyleGroup>;

export const commonStyles = (theme: Theme): StyleGroup => ({
  container: {
    flexDirection: "column",
    gap: "calc(2 * var(--rhythm))",
    [theme.breakpoints.down("md")]: {
      gap: "var(--rhythm)",
    },
    "@media print": {
      gap: 0,
    },
  },

  title: {
    mb: "calc(0.5 * var(--rhythm))",
    letterSpacing: "-0.0333em",
    [theme.breakpoints.down("md")]: {
      ml: "var(--rhythm)",
      mb: "calc(1 * var(--rhythm))",
    },
    "@media print": {
      ml: 0,
      fontSize: "12pt",
    },
  },

  card: {
    display: "flex",
    flexDirection: "column",
    gap: "calc(2 * var(--rhythm))",
    p: "calc(2 * var(--rhythm))",
    boxSizing: "border-box",
    [theme.breakpoints.down("md")]: {
      gap: "var(--rhythm)",
      p: "var(--rhythm)",
      borderRadius: 0,
    },
    "@media print": {
      p: 0,
      border: "none",
      boxShadow: "none",
    },
  },

  table: {
    width: "100%",
    "@media print": {
      tableLayout: "fixed",
      mb: "5mm",
    },
  },

  caption: {
    fontStyle: "italic",
    fontSize: "var(--font-size-s)",
    padding: "0 !important",
    mt: "var(--rhythm)",
    "@media print": {
      fontSize: "8pt !important",
    },
  },

  thead: {
    background: "transparent",
    ".MuiTableCell-root": {
      lineHeight: 1,
      color: "var(--color-black)",
      "&:first-of-type": { textAlign: "left" },
    },
  },

  tbody: {
    ".MuiTableCell-root": {
      fontWeight: 100,
      textAlign: "center",
      "&:first-of-type": { textAlign: "left" },
    },
  },

  cell: {
    paddingY: "calc(0.5 * var(--rhythm))",
    fontSize: "var(--font-size-m)",
    lineHeight: 1,
    border: "none",
    borderBottom: "1px solid var(--color-border)",
    sub: {
      lineHeight: 0,
    },
    sup: {
      lineHeight: 0,
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "var(--font-size-m)",
    },
    "@media print": {
      paddingY: "1mm",
      fontSize: "8pt",
    },
  },

  label: {
    mb: "calc(0.5 * var(--rhythm))",
    "@media print": {
      ml: 0,
      fontSize: "10pt",
    },
  },

  met: {
    fontWeight: "500",
    color: "var(--color-emerald-dark)",
  },

  notMet: {
    fontWeight: "500",
    color: "var(--color-red)",
  },

  tooHigh: {
    fontWeight: "500",
    color: "var(--color-orange-dark)",
  },
});

export default (theme: Theme): Styles => {
  const styles = commonStyles(theme);

  return {
    LuminanceTable: {
      ...styles,

      table: {
        ...styles.table,
        ".MuiTableCell-root": styles.cell,
      },
    },

    Criteria: {
      ...styles,

      table: {
        ...styles.table,
        "@media print": {
          display: "none",
        },
        ".MuiTableRow-root": {
          background: "transparent",
        },
        ".MuiTableCell-root": {
          ...styles.cell,
          "&:last-of-type": {
            textAlign: "right",
          },
        },
      },

      tbody: {
        ...styles.tbody,
        ".MuiTableCell-root": {
          "&:first-of-type": { textAlign: "left" },
          "&:last-of-type": { textAlign: "right" },
        },
      },
    },

    RoadState: {
      ...styles,

      container: {
        display: "none",
        displayPrint: "block",
      },

      table: {
        "@media print": {
          displayPrint: "table",
          mb: "5mm",
        },
      },
    },

    IlluminanceBorders: {
      ...styles,

      table: {
        ...styles.table,

        ".MuiTableCell-root": styles.cell,
      },
    },

    IlluminanceTable: {
      ...styles,

      table: {
        ...styles.table,
        ".MuiTableCell-root": styles.cell,
      },
    },
  };
};
